import React, { useRef, useEffect, useState } from "react";
import {
  Dialog,
  DialogPortal,
  DialogOverlay,
} from "../ui/dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Button } from "../ui/button";
import { Slider } from "../ui/slider";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import {
  X,
  Settings,
  Clock,
  CalendarRange,
  CalendarDays,
  Camera,
  Sun,
  Moon,
  Monitor,
  Palette,
  ZoomIn,
  ZoomOut,
  Bell,
  BellOff,
  RotateCcw,
  Upload,
  Check,
  Sunrise,
  VolumeX,
  Ban,
  Plus,
  Trash2,
  ArrowRightLeft,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { THEME_COLORS, VIEW_MODES, THEME_MODES } from "../../hooks/useTVSettings";

/**
 * Modular TV Settings Dialog component.
 * Shared between TeleView and PrayerTimesView.
 * Single scrollable page layout with all settings visible.
 */
export function TVSettingsDialog({
  open,
  onOpenChange,
  settings,
  onViewChange,
  onManageImages,
  isImageView = false,
  isTeleView = false,
}) {
  const colorInputRef = useRef(null);
  const inactivityTimerRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    const resetInactivityTimer = () => {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = setTimeout(() => onOpenChange(false), 30000);
    };

    const activityEvents = ["pointerdown", "pointermove", "keydown", "wheel", "touchstart"];
    activityEvents.forEach((eventName) => window.addEventListener(eventName, resetInactivityTimer));
    resetInactivityTimer();

    return () => {
      activityEvents.forEach((eventName) => window.removeEventListener(eventName, resetInactivityTimer));
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    };
  }, [open, onOpenChange]);

  const {
    viewMode,
    setViewMode,
    showTomorrow,
    setShowTomorrow,
    themeMode,
    setThemeMode,
    primaryColor,
    setPrimaryColor,
    setCustomColor,
    isDark,
    colorName,
    // Custom colors
    customColors = [],
    addCustomColor,
    removeCustomColor,
    clearCustomColors,
    MAX_CUSTOM_COLORS = 7,
    // Other settings
    zoomLevel,
    setZoomLevel,
    adhanEnabled,
    setAdhanEnabled,
    iqamaEnabled,
    setIqamaEnabled,
    adhanCountdownMinutes,
    setAdhanCountdownMinutes,
    iqamaCountdownMinutes,
    setIqamaCountdownMinutes,
    iqamaScreenDurationMinutes,
    setIqamaScreenDurationMinutes,
    adhanScreenDurationMinutes,
    setAdhanScreenDurationMinutes,
    // Jummah silence screen
    jummahSilenceEnabled,
    setJummahSilenceEnabled,
    jummahSilenceDurationMinutes,
    setJummahSilenceDurationMinutes,
    // Forbidden prayer screen
    forbiddenScreenEnabled,
    setForbiddenScreenEnabled,
    forbiddenScreenDurationMinutes,
    setForbiddenScreenDurationMinutes,
    // Tomorrow changes banner
    tomorrowChangesBanner,
    setTomorrowChangesBanner,
    resetToDefaults,
  } = settings;

  // View options
  const viewOptions = [
    { key: VIEW_MODES.TODAY, label: "Today", icon: Clock, description: "Single day prayer times" },
    { key: VIEW_MODES.MULTIDAY, label: "Multiday", icon: CalendarRange, description: "Today + tomorrow cards" },
    { key: VIEW_MODES.EVENTS, label: "Events", icon: CalendarDays, description: "Prayer times with events" },
    { key: VIEW_MODES.IMAGES, label: "Images", icon: Camera, description: "Prayer times with slideshow" },
  ];

  // Theme mode options
  const themeModeOptions = [
    { key: THEME_MODES.LIGHT, label: "Light", icon: Sun },
    { key: THEME_MODES.DARK, label: "Dark", icon: Moon },
    { key: THEME_MODES.SYSTEM, label: "System", icon: Monitor },
    { key: THEME_MODES.AUTO, label: "Auto", icon: Clock, description: "Light at sunrise, dark at maghrib" },
  ];

  // Color options
  const colorOptions = Object.entries(THEME_COLORS).map(([name, color]) => ({
    name,
    color,
  }));

  const handleViewChange = (newView) => {
    setViewMode(newView);
    if (onViewChange) {
      onViewChange(newView);
    }
  };

  const handleCustomColorClick = () => {
    colorInputRef.current?.click();
  };

  const handleCustomColorChange = (e) => {
    const color = e.target.value;
    setCustomColor(color);
  };

  // Show zoom for TeleView events/images modes
  const showZoom = isTeleView && (viewMode === VIEW_MODES.EVENTS || viewMode === VIEW_MODES.IMAGES);
  // Show image management for images mode
  const showImageManagement = viewMode === VIEW_MODES.IMAGES;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className={cn(
            "fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50",
            "flex flex-col w-[70vw] min-w-[400px] max-w-[900px] h-[85vh] max-h-[850px]",
            "border rounded-2xl shadow-2xl",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            isDark
              ? "bg-[#0f0f0f] text-white border-white/10"
              : "bg-white text-slate-900 border-slate-200"
          )}
        >
          {/* Accessibility: Hidden title for screen readers */}
          <DialogPrimitive.Title className="sr-only">
            Display Settings
          </DialogPrimitive.Title>

          {/* Header */}
          <div className={cn(
            "flex items-center justify-between px-6 py-5 border-b shrink-0",
            isDark ? "border-white/10" : "border-slate-200"
          )}>
            <div className="flex items-center gap-4">
              <div
                className="p-3 rounded-xl"
                style={{ backgroundColor: `${primaryColor}20` }}
              >
                <Settings className="h-6 w-6" style={{ color: primaryColor }} />
              </div>
              <div>
                <h2 className="text-xl font-semibold">Display Settings</h2>
                <p className={cn("text-sm", isDark ? "text-white/60" : "text-slate-500")}>
                  Configure your TV display
                </p>
              </div>
            </div>
            <DialogPrimitive.Close className={cn(
              "p-2.5 rounded-lg transition-colors",
              isDark ? "hover:bg-white/10" : "hover:bg-slate-100"
            )}>
              <X className="h-6 w-6" />
            </DialogPrimitive.Close>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-8">
            {/* ===== VIEW MODE SECTION ===== */}
            <section>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Clock className="h-5 w-5" style={{ color: primaryColor }} />
                View Mode
              </h3>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {viewOptions.map((option) => (
                  <button
                    key={option.key}
                    onClick={() => handleViewChange(option.key)}
                    className={cn(
                      "flex flex-col items-center gap-2 p-4 rounded-xl border-2 text-center transition-all",
                      viewMode === option.key
                        ? ""
                        : isDark
                          ? "border-white/10 hover:bg-white/5"
                          : "border-slate-200 hover:bg-slate-50"
                    )}
                    style={{
                      borderColor: viewMode === option.key ? primaryColor : undefined,
                      backgroundColor: viewMode === option.key
                        ? isDark ? `${primaryColor}15` : `${primaryColor}10`
                        : undefined,
                    }}
                  >
                    <option.icon
                      className="h-6 w-6"
                      style={{ color: viewMode === option.key ? primaryColor : undefined }}
                    />
                    <div>
                      <p className="font-medium">{option.label}</p>
                      <p className={cn("text-xs", isDark ? "text-white/50" : "text-slate-500")}>
                        {option.description}
                      </p>
                    </div>
                  </button>
                ))}
              </div>

              {/* Show Tomorrow toggle for Events/Images */}
              {(viewMode === VIEW_MODES.EVENTS || viewMode === VIEW_MODES.IMAGES) && (
                <div className={cn(
                  "flex items-center justify-between p-4 rounded-xl border mt-4",
                  isDark ? "border-white/10" : "border-slate-200"
                )}>
                  <div>
                    <Label className="text-base font-medium">Show Tomorrow</Label>
                    <p className={cn("text-sm", isDark ? "text-white/60" : "text-slate-500")}>
                      Display both today and tomorrow's content
                    </p>
                  </div>
                  <Switch
                    checked={showTomorrow}
                    onCheckedChange={setShowTomorrow}
                    style={{ backgroundColor: showTomorrow ? primaryColor : (isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)") }}
                  />
                </div>
              )}
            </section>

            {/* ===== THEME SECTION ===== */}
            <section>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Palette className="h-5 w-5" style={{ color: primaryColor }} />
                Theme & Colors
              </h3>

              {/* Appearance Mode */}
              <div className="mb-6">
                <Label className={cn("text-sm font-medium mb-3 block", isDark ? "text-white/70" : "text-slate-600")}>
                  Appearance
                </Label>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {themeModeOptions.map((option) => (
                    <button
                      key={option.key}
                      onClick={() => setThemeMode(option.key)}
                      className={cn(
                        "flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all",
                        themeMode === option.key
                          ? ""
                          : isDark
                            ? "border-white/10 hover:bg-white/5"
                            : "border-slate-200 hover:bg-slate-50"
                      )}
                      style={{
                        borderColor: themeMode === option.key ? primaryColor : undefined,
                        backgroundColor: themeMode === option.key
                          ? isDark ? `${primaryColor}15` : `${primaryColor}10`
                          : undefined,
                      }}
                    >
                      <option.icon
                        className="h-5 w-5 shrink-0"
                        style={{ color: themeMode === option.key ? primaryColor : undefined }}
                      />
                      <div className="min-w-0">
                        <p className="font-medium truncate">{option.label}</p>
                        {option.description && (
                          <p className={cn("text-xs truncate", isDark ? "text-white/50" : "text-slate-500")}>
                            {option.description}
                          </p>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Accent Color */}
              <div>
                <Label className={cn("text-sm font-medium mb-3 block", isDark ? "text-white/70" : "text-slate-600")}>
                  Accent Color: {colorName}
                </Label>
                
                {/* Preset Colors */}
                <div className="flex flex-wrap gap-3 py-1">
                  {colorOptions.map((option) => (
                    <button
                      key={option.name}
                      onClick={() => setPrimaryColor(option.color)}
                      className={cn(
                        "relative w-10 h-10 rounded-full border-2 transition-transform hover:scale-110 shrink-0",
                        primaryColor.toUpperCase() === option.color.toUpperCase() ? "ring-2 ring-offset-2" : ""
                      )}
                      style={{
                        backgroundColor: option.color,
                        borderColor: isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.1)",
                        ringColor: option.color,
                        ringOffsetColor: isDark ? "#0f0f0f" : "#ffffff",
                      }}
                      title={option.name}
                    >
                      {primaryColor.toUpperCase() === option.color.toUpperCase() && (
                        <Check className="absolute inset-0 m-auto h-4 w-4 text-white drop-shadow-md" />
                      )}
                    </button>
                  ))}

                  {/* Custom Colors */}
                  {customColors.map((color, index) => {
                    const isSelected = primaryColor.toUpperCase() === color.toUpperCase();
                    return (
                      <div key={`custom-${index}`} className="relative group shrink-0">
                        <button
                          onClick={() => setPrimaryColor(color)}
                          className={cn(
                            "relative w-10 h-10 rounded-full border-2 transition-transform hover:scale-110",
                            isSelected ? "ring-2 ring-offset-2" : ""
                          )}
                          style={{
                            backgroundColor: color,
                            borderColor: isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.1)",
                            ringColor: color,
                            ringOffsetColor: isDark ? "#0f0f0f" : "#ffffff",
                          }}
                          title={`Custom: ${color}`}
                        >
                          {isSelected && (
                            <Check className="absolute inset-0 m-auto h-4 w-4 text-white drop-shadow-md" />
                          )}
                        </button>
                        {/* Remove button on hover */}
                        {removeCustomColor && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeCustomColor(color);
                            }}
                            className={cn(
                              "absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center",
                              "opacity-0 group-hover:opacity-100 transition-opacity",
                              "bg-red-500 hover:bg-red-600 text-white shadow-md"
                            )}
                            title="Remove color"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    );
                  })}

                  {/* Custom Color Picker Button */}
                  <button
                    onClick={handleCustomColorClick}
                    className={cn(
                      "relative w-10 h-10 rounded-full border-2 border-dashed transition-transform hover:scale-110 flex items-center justify-center shrink-0"
                    )}
                    style={{
                      borderColor: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.2)",
                    }}
                    title={customColors.length >= MAX_CUSTOM_COLORS ? "Max custom colors reached (will replace oldest)" : "Add custom color"}
                  >
                    <Plus className="h-4 w-4" style={{ color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.4)" }} />
                  </button>
                  <input
                    ref={colorInputRef}
                    type="color"
                    value={primaryColor}
                    onChange={handleCustomColorChange}
                    className="sr-only"
                  />
                </div>

                {/* Clear custom colors button */}
                {customColors.length > 0 && clearCustomColors && (
                  <button
                    onClick={clearCustomColors}
                    className={cn(
                      "mt-3 text-xs flex items-center gap-1 transition-colors",
                      isDark ? "text-white/40 hover:text-white/70" : "text-slate-400 hover:text-slate-600"
                    )}
                  >
                    <Trash2 className="h-3 w-3" />
                    Clear custom colors
                  </button>
                )}
              </div>
            </section>

            {/* ===== ZOOM SECTION (TeleView events/images only) ===== */}
            {showZoom && (
              <section>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <ZoomIn className="h-5 w-5" style={{ color: primaryColor }} />
                  Panel Zoom
                </h3>
                <p className={cn("text-sm mb-4", isDark ? "text-white/60" : "text-slate-500")}>
                  Adjust the size of the events or images panel
                </p>

                <div className="flex items-center gap-4">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setZoomLevel(zoomLevel - 0.1)}
                    disabled={zoomLevel <= 0.5}
                    className="shrink-0"
                    style={{
                      borderColor: isDark ? "rgba(255,255,255,0.2)" : "#cbd5e1",
                      backgroundColor: "transparent",
                      color: isDark ? "#fff" : "#334155",
                    }}
                  >
                    <ZoomOut className="h-4 w-4" />
                  </Button>

                  <div className="flex-1">
                    <Slider
                      value={[zoomLevel]}
                      onValueChange={([v]) => setZoomLevel(v)}
                      min={0.5}
                      max={2.0}
                      step={0.1}
                      className="w-full"
                      style={{
                        "--slider-track-bg": `${primaryColor}30`,
                        "--slider-range-bg": primaryColor,
                        "--slider-thumb-border": `${primaryColor}80`,
                      }}
                    />
                  </div>

                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setZoomLevel(zoomLevel + 0.1)}
                    disabled={zoomLevel >= 2.0}
                    className="shrink-0"
                    style={{
                      borderColor: isDark ? "rgba(255,255,255,0.2)" : "#cbd5e1",
                      backgroundColor: "transparent",
                      color: isDark ? "#fff" : "#334155",
                    }}
                  >
                    <ZoomIn className="h-4 w-4" />
                  </Button>
                </div>

                <div className="flex items-center justify-between mt-2">
                  <span className={cn("text-sm", isDark ? "text-white/60" : "text-slate-500")}>
                    {Math.round(zoomLevel * 100)}%
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setZoomLevel(1.0)}
                    className={cn("text-sm", isDark ? "text-white/60 hover:text-white" : "text-slate-500")}
                  >
                    Reset to 100%
                  </Button>
                </div>
              </section>
            )}

            {/* ===== IMAGE MANAGEMENT SECTION ===== */}
            {showImageManagement && (
              <section>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Camera className="h-5 w-5" style={{ color: primaryColor }} />
                  Slideshow Images
                </h3>
                <p className={cn("text-sm mb-4", isDark ? "text-white/60" : "text-slate-500")}>
                  Manage images displayed in the slideshow view
                </p>

                <Button
                  onClick={() => {
                    if (onManageImages) onManageImages();
                  }}
                  className="w-full"
                  style={{ backgroundColor: primaryColor, color: "#fff" }}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Manage Images
                </Button>
              </section>
            )}

            {/* ===== ADHAN/IQAMA SECTION ===== */}
            <section>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Bell className="h-5 w-5" style={{ color: primaryColor }} />
                Adhan & Iqama Countdowns
              </h3>

              {/* Adhan Settings */}
              <div className={cn(
                "p-4 rounded-xl border mb-4",
                isDark ? "border-white/10" : "border-slate-200"
              )}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Bell className="h-5 w-5" style={{ color: primaryColor }} />
                    <div>
                      <Label className="text-base font-medium">Adhan Countdown</Label>
                      <p className={cn("text-sm", isDark ? "text-white/60" : "text-slate-500")}>
                        Show countdown before adhan time
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={adhanEnabled}
                    onCheckedChange={setAdhanEnabled}
                    style={{ backgroundColor: adhanEnabled ? primaryColor : (isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)") }}
                  />
                </div>

                {adhanEnabled && (
                  <div className="ml-8 space-y-4">
                    <div className="flex items-center justify-between">
                      <span className={cn("text-sm", isDark ? "text-white/60" : "text-slate-500")}>
                        Start countdown before:
                      </span>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setAdhanCountdownMinutes(adhanCountdownMinutes - 1)}
                          disabled={adhanCountdownMinutes <= 0}
                          className="h-8 w-8 p-0"
                          style={{
                            borderColor: isDark ? "rgba(255,255,255,0.2)" : "#cbd5e1",
                            backgroundColor: "transparent",
                            color: isDark ? "#fff" : "#334155",
                          }}
                        >
                          -
                        </Button>
                        <span className="w-16 text-center font-medium">
                          {adhanCountdownMinutes} min
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setAdhanCountdownMinutes(adhanCountdownMinutes + 1)}
                          disabled={adhanCountdownMinutes >= 10}
                          className="h-8 w-8 p-0"
                          style={{
                            borderColor: isDark ? "rgba(255,255,255,0.2)" : "#cbd5e1",
                            backgroundColor: "transparent",
                            color: isDark ? "#fff" : "#334155",
                          }}
                        >
                          +
                        </Button>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className={cn("text-sm", isDark ? "text-white/60" : "text-slate-500")}>
                        Show adhan screen for:
                      </span>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setAdhanScreenDurationMinutes(adhanScreenDurationMinutes - 1)}
                          disabled={adhanScreenDurationMinutes <= 0}
                          className="h-8 w-8 p-0"
                          style={{
                            borderColor: isDark ? "rgba(255,255,255,0.2)" : "#cbd5e1",
                            backgroundColor: "transparent",
                            color: isDark ? "#fff" : "#334155",
                          }}
                        >
                          -
                        </Button>
                        <span className="w-16 text-center font-medium">
                          {adhanScreenDurationMinutes} min
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setAdhanScreenDurationMinutes(adhanScreenDurationMinutes + 1)}
                          disabled={adhanScreenDurationMinutes >= 5}
                          className="h-8 w-8 p-0"
                          style={{
                            borderColor: isDark ? "rgba(255,255,255,0.2)" : "#cbd5e1",
                            backgroundColor: "transparent",
                            color: isDark ? "#fff" : "#334155",
                          }}
                        >
                          +
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Iqama Settings */}
              <div className={cn(
                "p-4 rounded-xl border",
                isDark ? "border-white/10" : "border-slate-200"
              )}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <BellOff className="h-5 w-5" style={{ color: primaryColor }} />
                    <div>
                      <Label className="text-base font-medium">Iqama Screen</Label>
                      <p className={cn("text-sm", isDark ? "text-white/60" : "text-slate-500")}>
                        Show countdown and silence phones message
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={iqamaEnabled}
                    onCheckedChange={setIqamaEnabled}
                    style={{ backgroundColor: iqamaEnabled ? primaryColor : (isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)") }}
                  />
                </div>

                {iqamaEnabled && (
                  <div className="ml-8 space-y-4">
                    <div className="flex items-center justify-between">
                      <span className={cn("text-sm", isDark ? "text-white/60" : "text-slate-500")}>
                        Start countdown before:
                      </span>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setIqamaCountdownMinutes(iqamaCountdownMinutes - 1)}
                          disabled={iqamaCountdownMinutes <= 0}
                          className="h-8 w-8 p-0"
                          style={{
                            borderColor: isDark ? "rgba(255,255,255,0.2)" : "#cbd5e1",
                            backgroundColor: "transparent",
                            color: isDark ? "#fff" : "#334155",
                          }}
                        >
                          -
                        </Button>
                        <span className="w-16 text-center font-medium">
                          {iqamaCountdownMinutes} min
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setIqamaCountdownMinutes(iqamaCountdownMinutes + 1)}
                          disabled={iqamaCountdownMinutes >= 10}
                          className="h-8 w-8 p-0"
                          style={{
                            borderColor: isDark ? "rgba(255,255,255,0.2)" : "#cbd5e1",
                            backgroundColor: "transparent",
                            color: isDark ? "#fff" : "#334155",
                          }}
                        >
                          +
                        </Button>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className={cn("text-sm", isDark ? "text-white/60" : "text-slate-500")}>
                        Show silence screen for:
                      </span>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setIqamaScreenDurationMinutes(iqamaScreenDurationMinutes - 1)}
                          disabled={iqamaScreenDurationMinutes <= 0}
                          className="h-8 w-8 p-0"
                          style={{
                            borderColor: isDark ? "rgba(255,255,255,0.2)" : "#cbd5e1",
                            backgroundColor: "transparent",
                            color: isDark ? "#fff" : "#334155",
                          }}
                        >
                          -
                        </Button>
                        <span className="w-16 text-center font-medium">
                          {iqamaScreenDurationMinutes} min
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setIqamaScreenDurationMinutes(iqamaScreenDurationMinutes + 1)}
                          disabled={iqamaScreenDurationMinutes >= 30}
                          className="h-8 w-8 p-0"
                          style={{
                            borderColor: isDark ? "rgba(255,255,255,0.2)" : "#cbd5e1",
                            backgroundColor: "transparent",
                            color: isDark ? "#fff" : "#334155",
                          }}
                        >
                          +
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <p className={cn("text-xs mt-4", isDark ? "text-white/40" : "text-slate-400")}>
                These settings control when the countdown overlay appears and how long the iqama screen stays visible.
                The screen will dim during iqama to encourage phone silencing.
              </p>
            </section>

            {/* ===== JUMMAH SILENCE SECTION ===== */}
            <section>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <VolumeX className="h-5 w-5" style={{ color: primaryColor }} />
                Jummah Silence Screen
              </h3>

              <div className={cn(
                "p-4 rounded-xl border",
                isDark ? "border-white/10" : "border-slate-200"
              )}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <VolumeX className="h-5 w-5" style={{ color: primaryColor }} />
                    <div>
                      <Label className="text-base font-medium">Enable Jummah Screen</Label>
                      <p className={cn("text-sm", isDark ? "text-white/60" : "text-slate-500")}>
                        Show silence phones message during Jummah khutbah
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={jummahSilenceEnabled}
                    onCheckedChange={setJummahSilenceEnabled}
                    style={{ backgroundColor: jummahSilenceEnabled ? primaryColor : (isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)") }}
                  />
                </div>

                {jummahSilenceEnabled && (
                  <div className="ml-8 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className={cn("text-sm", isDark ? "text-white/60" : "text-slate-500")}>
                        Show screen for:
                      </span>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setJummahSilenceDurationMinutes(jummahSilenceDurationMinutes - 5)}
                          disabled={jummahSilenceDurationMinutes <= 5}
                          className="h-8 w-8 p-0"
                          style={{
                            borderColor: isDark ? "rgba(255,255,255,0.2)" : "#cbd5e1",
                            backgroundColor: "transparent",
                            color: isDark ? "#fff" : "#334155",
                          }}
                        >
                          -
                        </Button>
                        <span className="w-16 text-center font-medium">
                          {jummahSilenceDurationMinutes} min
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setJummahSilenceDurationMinutes(jummahSilenceDurationMinutes + 5)}
                          disabled={jummahSilenceDurationMinutes >= 60}
                          className="h-8 w-8 p-0"
                          style={{
                            borderColor: isDark ? "rgba(255,255,255,0.2)" : "#cbd5e1",
                            backgroundColor: "transparent",
                            color: isDark ? "#fff" : "#334155",
                          }}
                        >
                          +
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <p className={cn("text-xs mt-4", isDark ? "text-white/40" : "text-slate-400")}>
                If khutbah times are set, the screen will show during khutbah. Otherwise, it shows at Jummah time.
              </p>
            </section>

            {/* ===== FORBIDDEN PRAYER TIME SECTION ===== */}
            <section>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Ban className="h-5 w-5" style={{ color: primaryColor }} />
                Forbidden Prayer Time
              </h3>

              <div className={cn(
                "p-4 rounded-xl border",
                isDark ? "border-white/10" : "border-slate-200"
              )}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Sunrise className="h-5 w-5" style={{ color: primaryColor }} />
                    <div>
                      <Label className="text-base font-medium">After Sunrise Screen</Label>
                      <p className={cn("text-sm", isDark ? "text-white/60" : "text-slate-500")}>
                        Show "forbidden to pray" reminder after sunrise
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={forbiddenScreenEnabled}
                    onCheckedChange={setForbiddenScreenEnabled}
                    style={{ backgroundColor: forbiddenScreenEnabled ? primaryColor : (isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)") }}
                  />
                </div>

                {forbiddenScreenEnabled && (
                  <div className="ml-8 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className={cn("text-sm", isDark ? "text-white/60" : "text-slate-500")}>
                        Duration after sunrise:
                      </span>
                      <div className="flex gap-2">
                        {[10, 15, 20, 30].map((mins) => (
                          <Button
                            key={mins}
                            variant="outline"
                            size="sm"
                            onClick={() => setForbiddenScreenDurationMinutes(mins)}
                            className={cn(
                              "h-8 px-3",
                              forbiddenScreenDurationMinutes === mins && "ring-2"
                            )}
                            style={{
                              borderColor: forbiddenScreenDurationMinutes === mins ? primaryColor : (isDark ? "rgba(255,255,255,0.2)" : "#cbd5e1"),
                              backgroundColor: forbiddenScreenDurationMinutes === mins ? `${primaryColor}20` : "transparent",
                              color: isDark ? "#fff" : "#334155",
                              ringColor: primaryColor,
                            }}
                          >
                            {mins}m
                          </Button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <p className={cn("text-xs mt-4", isDark ? "text-white/40" : "text-slate-400")}>
                Displays a countdown showing how much time remains during the forbidden prayer period.
                Prayer times will still be visible underneath.
              </p>
            </section>

            {/* ===== TOMORROW CHANGES BANNER (PrayerTimesView only) ===== */}
            {!isTeleView && (
              <section>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <ArrowRightLeft className="h-5 w-5" style={{ color: primaryColor }} />
                  Tomorrow's Changes
                </h3>

                <div className={cn(
                  "p-4 rounded-xl border",
                  isDark ? "border-white/10" : "border-slate-200"
                )}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <ArrowRightLeft className="h-5 w-5" style={{ color: primaryColor }} />
                      <div>
                        <Label className="text-base font-medium">Show Changes Banner</Label>
                        <p className={cn("text-sm", isDark ? "text-white/60" : "text-slate-500")}>
                          Sliding banner showing tomorrow's changed prayer times
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={tomorrowChangesBanner}
                      onCheckedChange={setTomorrowChangesBanner}
                      style={{ backgroundColor: tomorrowChangesBanner ? primaryColor : (isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)") }}
                    />
                  </div>
                </div>

                <p className={cn("text-xs mt-4", isDark ? "text-white/40" : "text-slate-400")}>
                  When enabled, a scrolling banner at the bottom shows any prayer times that differ tomorrow.
                </p>
              </section>
            )}
          </div>

          {/* Footer */}
          <div className={cn(
            "flex items-center justify-between px-6 py-4 border-t shrink-0",
            isDark ? "border-white/10" : "border-slate-200"
          )}>
            <Button
              variant="ghost"
              onClick={resetToDefaults}
              className={cn(
                "gap-2",
                isDark ? "text-white/60 hover:text-white hover:bg-white/10" : "text-slate-500"
              )}
            >
              <RotateCcw className="h-4 w-4" />
              Reset All
            </Button>

            <Button
              onClick={() => onOpenChange(false)}
              style={{ backgroundColor: primaryColor, color: "#fff" }}
            >
              Done
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}

export default TVSettingsDialog;
